import React, { useEffect, useMemo, useState } from 'react';
import { FaPrint, FaSyncAlt } from 'react-icons/fa';
import api from '../../services/api';
import './Billing.css';
import { formatShortDate } from '../../utils/date';

const ClientLedger = () => {
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedClient, setSelectedClient] = useState({ id: '', type: '' });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0, closingBalance: 0 });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [custRes, compRes] = await Promise.all([
          api.get('/api/customers?limit=1000'),
          api.get('/api/companies?limit=1000')
        ]);
        setCustomers(custRes.data?.customers || custRes.data || []);
        setCompanies(compRes.data?.companies || compRes.data || []);
      } catch (e) {
        console.error('Failed to load customers/companies', e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const canFetch = useMemo(() => !!selectedClient.id, [selectedClient]);

  const fetchLedger = async () => {
    if (!canFetch) return;
    setLoading(true);
    try {
      const params = { from: from || undefined, to: to || undefined };
      if (selectedClient.type === 'customer') params.customer = selectedClient.id;
      else if (selectedClient.type === 'company') params.company = selectedClient.id;

      // Use the same relative /api/... pattern as the rest of the app so
      // it works consistently on localhost and on cPanel with the configured baseURL
      const res = await api.get('/api/billing/clients/ledger', { params });
      setRows(res.data?.rows || []);
      setTotals(res.data?.totals || { debit: 0, credit: 0, closingBalance: 0 });
    } catch (e) {
      console.error('Failed to load ledger', e);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on first valid selection and when dates change
  useEffect(() => {
    if (canFetch) {
      fetchLedger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, from, to]);

  const handleClientChange = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedClient({ id: '', type: '' });
      return;
    }
    const [type, id] = val.split(':');
    setSelectedClient({ type, id });
  };

  const getSelectedClientName = () => {
    if (!selectedClient.id) return '';
    if (selectedClient.type === 'customer') {
      const client = customers.find(c => c._id === selectedClient.id);
      return client ? `${client.firstName || ''} ${client.lastName || ''}`.trim() : '';
    } else {
      const client = companies.find(c => c._id === selectedClient.id);
      return client ? client.name : '';
    }
  };

  const handlePrint = () => {
    if (!canFetch || rows.length === 0) return;

    const clientName = getSelectedClientName();
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <html>
        <head>
          <title>Client Ledger - ${clientName}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { margin: 0; color: #2c3e50; font-size: 24px; text-transform: uppercase; }
            .report-meta { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 0.9em; border-bottom: 1px solid #eee; padding-bottom: 10px; }
            .client-info { font-size: 1.1em; color: #2c3e50; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background-color: #f8f9fa; color: #2c3e50; font-weight: 600; border: 1px solid #dee2e6; padding: 12px 8px; text-align: left; }
            td { border: 1px solid #dee2e6; padding: 10px 8px; font-size: 0.85em; }
            .text-right { text-align: right; }
            .amount { font-family: 'Courier New', Courier, monospace; }
            tfoot tr { font-weight: bold; background-color: #f8f9fa; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; font-size: 0.8em; color: #7f8c8d; }
            @media print {
              th { background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; }
              tfoot tr { background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Client Ledger Report</h1>
          </div>
          <div class="client-info">
            <strong>Client:</strong> ${clientName}
          </div>
          <div class="report-meta">
            <div>
              <strong>Period:</strong> ${from || 'Beginning'} to ${to || 'End'}
            </div>
            <div><strong>Generated:</strong> ${new Date().toLocaleString()}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Bill Number</th>
                <th class="text-right">Debit</th>
                <th class="text-right">Credit</th>
                <th class="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(r => `
                <tr>
                  <td>${formatShortDate(r.date)}</td>
                  <td>${r.billNumber || '-'}</td>
                  <td class="text-right amount">₹${Number(r.debit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right amount">₹${Number(r.credit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right amount">₹${Number(r.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2">TOTALS</td>
                <td class="text-right amount">₹${Number(totals.debit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td class="text-right amount">₹${Number(totals.credit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td class="text-right amount">₹${Number(totals.closingBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
          </table>
          <div class="footer">
            <span>Total Records: ${rows.length}</span>
            <span>&copy; ${new Date().getFullYear()} Billing & Inventory System</span>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="billing-container">
      <div className="billing-header">
        <div>
          <h1 className="billing-title">Client Ledger</h1>
          <p className="billing-subtitle">View client-wise transactions and running balance</p>
          {selectedClient.id && (
            <p className="selected-client-display" style={{ marginTop: '8px', fontWeight: 'bold', color: '#2c3e50' }}>
              Ledger for: {getSelectedClientName()}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" onClick={handlePrint} disabled={!canFetch || rows.length === 0}>
            <FaPrint /> Print Report
          </button>
          <button className="refresh-btn" onClick={fetchLedger} disabled={!canFetch || loading}>
            <FaSyncAlt className={loading ? 'fa-spin' : ''} /> {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-row">
          <select
            className="filter-select"
            value={selectedClient.id ? `${selectedClient.type}:${selectedClient.id}` : ""}
            onChange={handleClientChange}
          >
            <option value="">Select client</option>
            <optgroup label="Customers">
              {customers.map((c) => (
                <option key={c._id} value={`customer:${c._id}`}>
                  {`${c.firstName || ''} ${c.lastName || ''}`.trim()} {c.isQuotationOnly ? '(Quotation)' : ''}
                </option>
              ))}
            </optgroup>
            <optgroup label="Companies">
              {companies.map((c) => (
                <option key={c._id} value={`company:${c._id}`}>{c.name}</option>
              ))}
            </optgroup>
          </select>
          <input type="date" className="filter-select" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="filter-select" value={to} onChange={(e) => setTo(e.target.value)} />
          <button className="refresh-btn" onClick={fetchLedger} disabled={!canFetch || loading}>
            {loading ? 'Loading...' : 'Load Report'}
          </button>
        </div>
      </div>

      <div className="billing-table-wrapper">
        <table className="billing-table">
          <thead>
            <tr>
              <th>Purchase Date</th>
              <th>Bill Number</th>
              <th>Total Amount (Debit)</th>
              <th>Paid Amount (Credit)</th>
              <th>Remaining Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center' }}>{loading ? 'Loading...' : 'No records found.'}</td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{formatShortDate(r.date)}</td>
                  <td>{r.billNumber}</td>
                  <td>₹{Number(r.debit || 0).toFixed(2)}</td>
                  <td>₹{Number(r.credit || 0).toFixed(2)}</td>
                  <td>₹{Number(r.balance || 0).toFixed(2)}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>Totals</td>
              <td>₹{Number(totals.debit || 0).toFixed(2)}</td>
              <td>₹{Number(totals.credit || 0).toFixed(2)}</td>
              <td>₹{Number(totals.closingBalance || 0).toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ClientLedger;


